import React, { useEffect, useState, useCallback } from "react";
import { api } from "@finance/lib/api";
import { toast } from "sonner";
import Layout, { PageHeader } from "@finance/components/Layout";
import ActionDialog from "@finance/components/ActionDialog";
import { ActionCard, CategoryBadge, TagChips, PriorityBadge } from "@finance/components/ActionBits";
import { CATEGORIES, catColor, sortActions, tagClass } from "@finance/lib/actionColors";
import { Button } from "@finance/components/ui/button";
import { Checkbox } from "@finance/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@finance/components/ui/select";
import { Plus, Rows, SquaresFour, Kanban, X } from "@phosphor-icons/react";

const VIEW_KEY = "lynx_actions_view";
const BOARDBY_KEY = "lynx_actions_boardby";

const STATUS_COLS = ["Pending", "Completed"];
const STATUS_DOT = { Pending: "bg-amber-500", Completed: "bg-emerald-500" };

function ViewSwitch({ view, onChange }) {
  const opts = [
    { k: "list", icon: Rows, label: "List" },
    { k: "grid", icon: SquaresFour, label: "Grid" },
    { k: "board", icon: Kanban, label: "Board" },
  ];
  return (
    <div className="inline-flex rounded-sm border border-gray-200 bg-white p-0.5" data-testid="actions-view-switch">
      {opts.map((o) => (
        <button key={o.k} data-testid={`view-${o.k}`} onClick={() => onChange(o.k)}
          className={`inline-flex items-center gap-1.5 rounded-[3px] px-2.5 py-1 text-xs font-semibold transition-colors ${view === o.k ? "bg-[#1B4383] text-white" : "text-gray-500 hover:bg-gray-100"}`}>
          <o.icon size={15} weight={view === o.k ? "fill" : "regular"} />{o.label}
        </button>
      ))}
    </div>
  );
}

export default function MyActions() {
  const [actions, setActions] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [view, setView] = useState(() => localStorage.getItem(VIEW_KEY) || "list");
  const [boardBy, setBoardBy] = useState(() => localStorage.getItem(BOARDBY_KEY) || "category");
  const [cat, setCat] = useState("all");
  const [sort, setSort] = useState("assigned");
  const [tagFilter, setTagFilter] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [overCol, setOverCol] = useState(null);

  const load = useCallback(() => { api.get("/actions/my").then((r) => setActions(r.data)); }, []);
  useEffect(() => { load(); api.get("/users").then((r) => setUsers(r.data)); }, [load]);
  const setViewP = (v) => { setView(v); localStorage.setItem(VIEW_KEY, v); };
  const setBoardByP = (v) => { setBoardBy(v); localStorage.setItem(BOARDBY_KEY, v); };

  const toggle = async (a) => { await api.patch(`/actions/${a.id}/toggle`); load(); };
  const onEdit = (x) => { setEditing(x); setOpen(true); };

  let filtered = cat === "all" ? actions : actions.filter((a) => a.category === cat);
  if (tagFilter) filtered = filtered.filter((a) => (a.tags || []).some((t) => t.toLowerCase() === tagFilter.toLowerCase()));
  filtered = sortActions(filtered, sort);
  const pending = filtered.filter((a) => a.status === "Pending");
  const done = filtered.filter((a) => a.status === "Completed");

  const catCounts = CATEGORIES.map((c) => ({ c, n: actions.filter((a) => a.category === c).length })).filter((x) => x.n);

  // Board drag-and-drop
  const boardCols = boardBy === "status" ? STATUS_COLS : CATEGORIES.filter((c) => filtered.some((a) => a.category === c));
  const colItems = (col) => filtered.filter((a) => (boardBy === "status" ? a.status === col : a.category === col));
  const onDrop = async (col) => {
    const a = actions.find((x) => x.id === dragId);
    setDragId(null); setOverCol(null);
    if (!a) return;
    const same = boardBy === "status" ? a.status === col : a.category === col;
    if (same) return;
    try {
      await api.patch(`/actions/${a.id}/move`, boardBy === "status" ? { status: col } : { category: col });
      toast.success(boardBy === "status" ? `Moved to ${col}` : `Category changed to ${col}`);
      load();
    } catch { toast.error("Could not move the action"); }
  };

  return (
    <Layout>
      <PageHeader title="My Actions" subtitle="Tasks assigned to you"
        actions={<Button data-testid="new-action-btn" className="bg-[#1B4383] hover:bg-[#122E5C]" onClick={() => { setEditing(null); setOpen(true); }}><Plus size={16} className="mr-1" /> New Action</Button>}
      />
      <div className="mx-auto max-w-6xl p-8 data-[board=true]:max-w-none" data-board={view === "board"}>
        {/* Toolbar */}
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <Select value={cat} onValueChange={setCat}>
              <SelectTrigger className="h-9 w-44" data-testid="filter-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="h-9 w-44" data-testid="sort-actions"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="assigned">Sort: Newest</SelectItem>
                <SelectItem value="due">Sort: Due date</SelectItem>
                <SelectItem value="priority">Sort: Priority</SelectItem>
                <SelectItem value="category">Sort: Category</SelectItem>
              </SelectContent>
            </Select>
            {view === "board" && (
              <div className="inline-flex rounded-sm border border-gray-200 bg-white p-0.5" data-testid="board-group-switch">
                {[{ k: "category", label: "By category" }, { k: "status", label: "By status" }].map((o) => (
                  <button key={o.k} data-testid={`board-by-${o.k}`} onClick={() => setBoardByP(o.k)}
                    className={`rounded-[3px] px-2.5 py-1 text-xs font-semibold transition-colors ${boardBy === o.k ? "bg-gray-800 text-white" : "text-gray-500 hover:bg-gray-100"}`}>{o.label}</button>
                ))}
              </div>
            )}
          </div>
          <ViewSwitch view={view} onChange={setViewP} />
        </div>

        {/* Active tag filter banner */}
        {tagFilter && (
          <div className="mb-4 flex items-center gap-2 text-sm" data-testid="tag-filter-banner">
            <span className="text-gray-500">Filtered by tag:</span>
            <span className={`inline-flex items-center gap-1 rounded-sm border px-1.5 py-0.5 text-[11px] font-medium ${tagClass(tagFilter)}`}>#{tagFilter}</span>
            <button data-testid="clear-tag-filter" onClick={() => setTagFilter(null)} className="inline-flex items-center gap-0.5 text-xs text-gray-400 hover:text-red-500"><X size={12} />clear</button>
          </div>
        )}

        {/* Category quick-chips */}
        {catCounts.length > 0 && (
          <div className="mb-5 flex flex-wrap gap-1.5" data-testid="category-chips">
            <button onClick={() => setCat("all")} className={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${cat === "all" ? "border-gray-800 bg-gray-800 text-white" : "border-gray-200 bg-white text-gray-500 hover:bg-gray-50"}`}>All · {actions.length}</button>
            {catCounts.map(({ c, n }) => {
              const col = catColor(c); const active = cat === c;
              return (
                <button key={c} data-testid={`chip-${c}`} onClick={() => setCat(active ? "all" : c)}
                  className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold transition-all ${active ? col.badge + " ring-1 ring-offset-1" : "border-gray-200 bg-white text-gray-500 hover:bg-gray-50"}`}>
                  <span className={`h-1.5 w-1.5 rounded-full ${col.dot}`} />{c} · {n}
                </button>
              );
            })}
          </div>
        )}

        {filtered.length === 0 && <div className="rounded-lg border border-dashed border-gray-200 p-10 text-center text-sm text-gray-400" data-testid="actions-empty">No actions here yet.</div>}

        {/* LIST VIEW */}
        {view === "list" && filtered.length > 0 && (
          <>
            <div className="lx-label mb-2">Pending ({pending.length})</div>
            <div className="space-y-2">
              {pending.map((a) => <ListRow key={a.id} a={a} onToggle={toggle} onEdit={onEdit} onTagClick={setTagFilter} />)}
              {pending.length === 0 && <div className="rounded-sm border border-dashed border-gray-200 p-4 text-center text-xs text-gray-400">Nothing pending 🎉</div>}
            </div>
            {done.length > 0 && (
              <>
                <div className="lx-label mb-2 mt-6">Completed ({done.length})</div>
                <div className="space-y-2">{done.map((a) => <ListRow key={a.id} a={a} onToggle={toggle} onEdit={onEdit} onTagClick={setTagFilter} />)}</div>
              </>
            )}
          </>
        )}

        {/* GRID VIEW */}
        {view === "grid" && filtered.length > 0 && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="actions-grid">
            {[...pending, ...done].map((a) => <ActionCard key={a.id} a={a} onToggle={toggle} onEdit={onEdit} onTagClick={setTagFilter} />)}
          </div>
        )}

        {/* BOARD VIEW */}
        {view === "board" && filtered.length > 0 && (
          <div className="flex gap-4 overflow-x-auto pb-4" data-testid="actions-board">
            {boardCols.map((col) => {
              const items = colItems(col);
              const dot = boardBy === "status" ? (STATUS_DOT[col] || "bg-gray-400") : catColor(col).dot;
              const isOver = overCol === col;
              return (
                <div key={col} data-testid={`board-col-${col}`}
                  onDragOver={(e) => { e.preventDefault(); if (overCol !== col) setOverCol(col); }}
                  onDragLeave={() => setOverCol((c) => (c === col ? null : c))}
                  onDrop={() => onDrop(col)}
                  className={`flex min-w-[240px] flex-1 flex-col rounded-xl p-2.5 transition-colors ${isOver ? "bg-[#1B4383]/10 ring-2 ring-[#1B4383]/40" : "bg-gray-50/70"}`}>
                  <div className="mb-2 flex items-center justify-between px-1">
                    <span className="inline-flex items-center gap-1.5 text-xs font-bold text-gray-700"><span className={`h-2 w-2 rounded-full ${dot}`} />{col}</span>
                    <span className="text-xs font-semibold text-gray-400">{items.length}</span>
                  </div>
                  <div className="min-h-[40px] space-y-2">
                    {items.map((a) => (
                      <div key={a.id} draggable onDragStart={() => setDragId(a.id)} onDragEnd={() => { setDragId(null); setOverCol(null); }}
                        className={`cursor-grab active:cursor-grabbing ${dragId === a.id ? "opacity-40" : ""}`} data-testid={`board-card-${a.id}`}>
                        <ActionCard a={a} onToggle={toggle} onEdit={onEdit} onTagClick={setTagFilter} compact />
                      </div>
                    ))}
                    {items.length === 0 && <div className="rounded-sm border border-dashed border-gray-200 py-3 text-center text-[11px] text-gray-300">Drop here</div>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <ActionDialog open={open} onOpenChange={setOpen} users={users} initial={editing} onSaved={load} />
    </Layout>
  );
}

function ListRow({ a, onToggle, onEdit, onTagClick }) {
  const c = catColor(a.category);
  const done = a.status === "Completed";
  const overdue = !done && a.due_date && a.due_date < new Date().toISOString().slice(0, 10);
  return (
    <div data-testid={`action-card-${a.id}`} className={`soft-card soft-card--hover lx-row-enter group flex items-start gap-3 p-4 ${done ? "opacity-60 hover:opacity-100" : ""}`}>
      <Checkbox checked={done} data-testid={`action-toggle-${a.id}`} onCheckedChange={() => onToggle(a)} className="mt-0.5" />
      <div className="flex-1">
        <div className={`text-sm ${done ? "text-gray-400 line-through" : "text-gray-900 dark:text-gray-100"}`}>{a.details}</div>
        <div className="mt-1.5 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[11px] text-gray-500">
          <span className="inline-flex items-center gap-1.5">
            <span className={`h-2 w-2 rounded-full ${done ? "bg-gray-300" : overdue ? "bg-red-500" : c.dot}`} />
            <span className="font-medium text-gray-600 dark:text-gray-300">{a.category}</span>
          </span>
          {a.priority && a.priority !== "Medium" && <><span className="text-gray-300">·</span><span className={`font-semibold ${a.priority === "High" ? "text-red-600" : "text-gray-400"}`}>{a.priority}</span></>}
          {overdue && <><span className="text-gray-300">·</span><span className="font-semibold uppercase tracking-wide text-red-600">Overdue</span></>}
          {(a.tags || []).length > 0 && <><span className="text-gray-300">·</span><TagChips tags={a.tags} onTagClick={onTagClick} /></>}
          <span className="text-gray-300">·</span>
          <span className="text-gray-400">{a.assigned_to_name}</span>
          {a.due_date && <><span className="text-gray-300">·</span><span className={`lx-num ${overdue ? "font-semibold text-red-600" : "text-gray-500"}`} data-testid={`action-due-${a.id}`}>Due {a.due_date.slice(0, 10)}</span></>}
        </div>
      </div>
      <button data-testid={`action-edit-${a.id}`} onClick={() => onEdit(a)} className="text-xs text-gray-400 opacity-0 transition-opacity hover:text-gray-700 group-hover:opacity-100">Edit</button>
    </div>
  );
}
